/**
 * Gift with purchase.
 *
 * checkGWP() inspects the cart and adds or removes the free gift line, then
 * hands the resulting cart (with rendered sections) to `callback`.
 *
 * The callback is what re-renders the cart in cart.js, so it MUST run exactly
 * once on every path — including failures. If it is skipped, the drawer keeps
 * the old markup and the subtotal silently goes stale.
 */

const GWP_LOG_PREFIX = '🎁 [GWP]';

function gwpSettings() {
  return window.shop?.giftWithPurchase;
}

function gwpLog(...args) {
  if (gwpSettings()?.enableLog) console.log(GWP_LOG_PREFIX, ...args);
}

/**
 * Public entry point: wraps checkGWPInner so the callback is guaranteed to fire
 * once, even if the inner logic throws (a missing window.shop, or Currency not
 * yet loaded from the deferred currencies.js CDN script).
 */
async function checkGWP(cart, callback = null, callbackOnlyOnSuccess = false) {
  let done = false;
  const once =
    typeof callback === 'function'
      ? function (...args) {
          if (done) return;
          done = true;
          return callback.apply(this, args);
        }
      : callback;

  try {
    return await checkGWPInner(cart, once, callbackOnlyOnSuccess);
  } catch (error) {
    console.error(`${GWP_LOG_PREFIX} failed, falling back to plain cart render:`, error);
    if (once) once();
  }
}

async function checkGWPInner(cart, callback = null, callbackOnlyOnSuccess = false) {
  gwpLog('0.0 | IN checkGWP()', { cart, callback, callbackOnlyOnSuccess });

  const {
    giftVariantIds,
    giftProductIds,
    giftEnable,
    giftProductsAvailable,
    minCartPrice,
    eligibleProductIds,
    onePerEligible,
  } = gwpSettings();

  const productIds = giftProductIds.map((id) => parseInt(id));
  const variantIds = giftVariantIds.map((id) => parseInt(id));
  const available = giftProductsAvailable.map((value) => value == 'true');

  const asyncCart = cart || (await getCart());
  const isRemoved = sessionStorage.getItem('gwp') === 'removed';
  const GWPPopup = document.querySelector('gwp-popup');

  const findGift = () =>
    asyncCart.items.find((item) => variantIds.includes(item.variant_id) && item.properties._isGWP);

  const totalGiftQuantity = asyncCart.items.reduce(
    (total, { variant_id, quantity }) => (variantIds.includes(variant_id) ? total + quantity : total),
    0
  );
  const hasFreeGift = findGift() !== undefined;

  const eligibleProductCount =
    eligibleProductIds === 'all-products'
      ? asyncCart.items.reduce(
          (total, item) => (variantIds.includes(item.variant_id) ? total : total + item.quantity),
          0
        )
      : asyncCart.items.reduce(
          (total, item) => (eligibleProductIds.includes(item.product_id) ? total + item.quantity : total),
          0
        );

  const requiredGiftQuantity = onePerEligible ? eligibleProductCount : eligibleProductCount > 0 ? 1 : 0;

  gwpLog('1.x | settings', { variantIds, productIds, giftEnable, available, minCartPrice, eligibleProductIds, onePerEligible });
  gwpLog('2.x | cart', { asyncCart, isRemoved, totalGiftQuantity, hasFreeGift, GWPPopup });
  gwpLog('3.x | eligibility', { eligibleProductIds, eligibleProductCount, requiredGiftQuantity });

  // Nothing to render on our own; hand control back to the caller.
  const passThrough = () => {
    if (typeof callback === 'function' && !callbackOnlyOnSuccess) callback();
  };

  // Removing the gift: use the line item `key`, not the variant or product id —
  // the gift line carries a `_isGWP` property, so its key is the only value
  // change.js can resolve unambiguously.
  const removeGift = () => {
    const giftKey = findGift()?.key;
    if (giftKey) {
      cartAjaxFetch(routes.cart_change_url, giftKey, totalGiftQuantity - 1, callback);
    } else if (typeof callback === 'function') {
      callback();
    }
  };

  // --- Gift programme is off, dismissed by the shopper, or nothing in stock ---
  if (!giftEnable || isRemoved || available.every((isAvailable) => !isAvailable)) {
    if (hasFreeGift) removeGift();
    else passThrough();
    return;
  }

  // --- Gift programme is live ---
  if (giftEnable && eligibleProductIds && variantIds && available.some((isAvailable) => isAvailable)) {
    const cartTotalUSD = Currency.convert(asyncCart.total_price, asyncCart.currency, 'USD');

    const qualifiesForGift = totalGiftQuantity < requiredGiftQuantity && minCartPrice <= cartTotalUSD;

    const hasEligibleItem =
      eligibleProductIds === 'all-products' ||
      asyncCart.items.map((item) => item.product_id).some((id) => eligibleProductIds.includes(id));

    const shouldRemoveGift =
      requiredGiftQuantity < totalGiftQuantity ||
      (totalGiftQuantity > 0 && cartTotalUSD < minCartPrice) ||
      (asyncCart.items.length === 1 && asyncCart.items[0].quantity == 1 && hasFreeGift) ||
      (eligibleProductIds != 'all-products' &&
        asyncCart.items
          .map((item) => item.product_id)
          .every((id) => productIds.includes(id) || !eligibleProductIds.includes(id)));

    if (qualifiesForGift) {
      if (!hasEligibleItem) {
        passThrough();
      } else if (productIds.length > 1 && GWPPopup) {
        gwpLog('X.X | OPEN POPUP');
        GWPPopup.openGwpPopup(callback);
      } else {
        cartAjaxFetch(routes.cart_add_url, variantIds[0], 1, callback, { _isGWP: true });
      }
      return;
    }

    if (shouldRemoveGift) removeGift();
    else passThrough();
    return;
  }

  passThrough();
}

async function getCart() {
  const response = await fetch('/cart.json');
  if (response.status === 200) return response.json();
  throw new Error(`Failed to get request, Shopify returned ${response.status} ${response.statusText}`);
}

function getCartSections() {
  return [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
  ];
}

function cartAjaxFetch(url, id, quantity, callback, properties = null) {
  // No id means there is nothing to change — don't build a request out of
  // `undefined`, just let the caller render the cart it already has.
  if (id == null) {
    if (typeof callback === 'function') callback();
    return;
  }

  const payload = {
    id: id.toString(),
    quantity,
    sections: getCartSections().map((section) => section.section),
    sections_url: window.location.pathname,
  };
  if (properties) payload.properties = properties;

  gwpLog('X.0 | AJAX body', payload);

  fetch(url, { ...fetchConfig(), body: JSON.stringify(payload) })
    .then((response) => response.json())
    .then((data) => {
      if (typeof callback !== 'function') return;

      // Shopify signals a rejected cart operation with a `status` field. The
      // gift did not change, but the cart still has to be re-rendered —
      // swallowing the callback here is what left the subtotal stale.
      if (data.status) {
        console.error(`${GWP_LOG_PREFIX} cart request rejected:`, data);
        callback();
      } else {
        callback(data);
      }
    })
    .catch((error) => {
      console.error(error);
      if (typeof callback === 'function') callback();
    });
}

window.gwpCurrentCallback = null;
